/**
 * Cuál es la entrada activa de una dirección: una sola, la más específica que
 * la cubra.
 *
 * Cada entrada cubre su propia dirección (con los parámetros que lleve su `to`)
 * y todo lo que cuelga debajo, más sus patrones `cubre`. Entre las que
 * coinciden gana la de mayor puntaje: segmentos fijos coincidentes más
 * parámetros coincidentes; a igual puntaje, la primera del registro. Función
 * pura, sin React.
 */
import { entradasDe, type Entrada, type EntradaUbicada, type Patron, type Seccion } from './registro';

export interface Ubicacion {
  pathname: string;
  /** Con o sin el `?` inicial. */
  search: string;
}

export interface EntradaActiva extends EntradaUbicada {
  /**
   * Cantidad de segmentos de la dirección que pertenecen a la entrada. Lo que
   * viene después (un detalle, un formulario) cuelga de ella. Son los segmentos
   * fijos iniciales del patrón que ganó.
   */
  segmentosBase: number;
  /**
   * Segmentos que el patrón que ganó declara como `:parametro` justo después de
   * la base (el id de un detalle). Las migas los muestran como «Detalle».
   */
  segmentosDeParametro: number;
}

const segmentos = (path: string) => path.split('/').filter(Boolean);

/** Patrón implícito de la propia dirección de la entrada (`to`, con su `?tab=`). */
function patronPropio(entrada: Entrada): Patron {
  const [path, consulta] = entrada.to.split('?');
  if (!consulta) return { path };
  const params: Record<string, string[]> = {};
  for (const [k, v] of new URLSearchParams(consulta)) params[k] = [v];
  return { path, params };
}

/** Puntaje del patrón contra la ubicación, o null si no la cubre. */
function puntuar(patron: Patron, pathname: string, consulta: URLSearchParams): number | null {
  const del = segmentos(patron.path);
  const dir = segmentos(pathname);
  if (del.length > dir.length) return null;
  let puntaje = 0;
  for (let i = 0; i < del.length; i += 1) {
    if (del[i].startsWith(':')) continue;
    if (del[i] !== dir[i]) return null;
    puntaje += 1;
  }
  for (const [clave, validos] of Object.entries(patron.params ?? {})) {
    const valor = consulta.get(clave) ?? '';
    if (!validos.includes(valor)) return null;
    // Que el parámetro falte (`''`) no es una coincidencia con algo: no suma.
    // Si sumara, `/ai-hub` sin pestaña le ganaría a `/ai-hub/agents/:agentKey`.
    if (valor !== '') puntaje += 1;
  }
  return puntaje;
}

function medidasDe(patron: Patron): { base: number; parametros: number } {
  const del = segmentos(patron.path);
  const primerParametro = del.findIndex((s) => s.startsWith(':'));
  if (primerParametro === -1) return { base: del.length, parametros: 0 };
  let parametros = 0;
  while (del[primerParametro + parametros]?.startsWith(':')) parametros += 1;
  return { base: primerParametro, parametros };
}

export function resolverEntradaActiva(
  registro: Seccion[],
  ubicacion: Ubicacion,
): EntradaActiva | null {
  const consulta = new URLSearchParams(ubicacion.search);
  let mejor: { ubicada: EntradaUbicada; puntaje: number; base: number; parametros: number } | null = null;

  for (const ubicada of entradasDe(registro)) {
    const patrones = [patronPropio(ubicada.entrada), ...(ubicada.entrada.cubre ?? [])];
    for (const patron of patrones) {
      const puntaje = puntuar(patron, ubicacion.pathname, consulta);
      if (puntaje === null) continue;
      // `>` estricto: a igual puntaje se queda la primera del registro.
      if (!mejor || puntaje > mejor.puntaje) {
        mejor = { ubicada, puntaje, ...medidasDe(patron) };
      }
    }
  }
  return mejor
    ? { ...mejor.ubicada, segmentosBase: mejor.base, segmentosDeParametro: mejor.parametros }
    : null;
}
